class BricklineHeaderComponent extends HTMLElement {
  connectedCallback() {
    // Horizon's --header-group-height is only ever set by scripts that look
    // for a <header-component> element; Brickline's header is a
    // <brickline-header-component> instead, so that variable is never
    // populated here and silently falls back to 0px wherever it's used
    // (fixed-position submenu/backdrop/drawer offsets). Compute our own so
    // those offsets account for the real utility-bar + header height.
    this.updateHeaderGroupHeight();
    const headerGroup = document.querySelector('#header-group');
    if (headerGroup && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => this.updateHeaderGroupHeight());
      resizeObserver.observe(headerGroup);
    }

    this.toggle = this.querySelector('.brickline-header__menu-toggle');
    this.panel = this.querySelector('.brickline-header__mobile-menu');

    if (this.toggle && this.panel) {
      this.toggle.addEventListener('click', () => this.toggleMenu());
      this.panel.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => this.closeMenu());
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeMenu();
      });
      document.addEventListener('click', (event) => {
        if (this.toggle.getAttribute('aria-expanded') !== 'true') return;
        if (this.contains(event.target)) return;
        this.closeMenu();
      });
    }

    this.backdrop = this.querySelector('.brickline-header__backdrop');
    this.navClose = this.querySelector('.brickline-header__nav-close');
    this.navItems = Array.from(this.querySelectorAll('.brickline-header__nav-item'));
    this.navItems.forEach((item) => {
      const button = item.querySelector('.brickline-header__nav-link--has-submenu');
      if (button) button.addEventListener('click', () => this.toggleSubmenu(item));

      const sidebarTabs = Array.from(item.querySelectorAll('.brickline-header__shop-sidebar-item[data-shop-panel]'));
      sidebarTabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectShopPanel(item, tab));
      });
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeAllSubmenus());
    }

    if (this.navClose) {
      this.navClose.addEventListener('click', () => this.closeAllSubmenus());
    }

    if (this.navItems.length) {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAllSubmenus();
      });
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.closeAllSubmenus();
      });
    }

    this.mobileItems = Array.from(this.querySelectorAll('.brickline-header__mobile-item'));
    this.mobileItems.forEach((item) => {
      const button = item.querySelector('.brickline-header__mobile-link--has-submenu');
      const submenu = item.querySelector('.brickline-header__mobile-submenu');
      if (!button || !submenu) return;
      button.addEventListener('click', () => this.toggleMobileSubmenu(item, button));

      const backButton = submenu.querySelector('.brickline-header__mobile-back');
      if (backButton) backButton.addEventListener('click', () => this.toggleMobileSubmenu(item, button));
    });
  }

  updateHeaderGroupHeight() {
    const headerGroup = document.querySelector('#header-group');
    if (!headerGroup) return;
    const height = Math.round(headerGroup.getBoundingClientRect().height);
    document.body.style.setProperty('--brickline-header-group-height', `${height}px`);
  }

  toggleMenu() {
    const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) this.closeMenu();
    else this.openMenu();
  }

  openMenu() {
    this.toggle.setAttribute('aria-expanded', 'true');
    this.panel.classList.add('is-open');
    this.panel.removeAttribute('aria-hidden');
  }

  closeMenu() {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.closeAllSubmenus();
    this.closeAllMobileSubmenus();
  }

  toggleSubmenu(item) {
    const isOpen = item.classList.contains('is-open');
    this.closeAllSubmenus();
    if (!isOpen) this.openSubmenu(item);
  }

  openSubmenu(item) {
    item.classList.add('is-open');
    const button = item.querySelector('.brickline-header__nav-link--has-submenu');
    if (button) button.setAttribute('aria-expanded', 'true');
    if (this.backdrop) this.backdrop.classList.add('is-open');
    this.classList.add('has-submenu-open');
  }

  closeAllSubmenus() {
    this.navItems.forEach((item) => {
      item.classList.remove('is-open');
      const button = item.querySelector('.brickline-header__nav-link--has-submenu');
      if (button) button.setAttribute('aria-expanded', 'false');
    });
    if (this.backdrop) this.backdrop.classList.remove('is-open');
    this.classList.remove('has-submenu-open');
  }

  // Switches the active sidebar row / right-hand panel within the Shop
  // flyout — every panel is already rendered server-side, so this just
  // toggles visibility, no fetch involved.
  selectShopPanel(item, tab) {
    const targetId = tab.dataset.shopPanel;
    if (!targetId) return;

    item.querySelectorAll('.brickline-header__shop-sidebar-item[data-shop-panel]').forEach((otherTab) => {
      const isTarget = otherTab === tab;
      otherTab.classList.toggle('is-active', isTarget);
      otherTab.setAttribute('aria-selected', String(isTarget));
    });

    item.querySelectorAll('.brickline-header__shop-panel').forEach((panel) => {
      const isTarget = panel.id === targetId;
      panel.classList.toggle('is-active', isTarget);
      panel.hidden = !isTarget;
    });
  }

  toggleMobileSubmenu(item, button) {
    const isOpen = item.classList.contains('is-open');
    item.classList.toggle('is-open', !isOpen);
    button.setAttribute('aria-expanded', String(!isOpen));
  }

  closeAllMobileSubmenus() {
    this.mobileItems.forEach((item) => {
      item.classList.remove('is-open');
      const button = item.querySelector('.brickline-header__mobile-link--has-submenu');
      if (button) button.setAttribute('aria-expanded', 'false');
    });
  }
}

if (!customElements.get('brickline-header-component')) {
  customElements.define('brickline-header-component', BricklineHeaderComponent);
}
